import { Head } from '@inertiajs/react'
import AppShell from '@/components/AppShell'
import {
  Settings2,
  BarChart3,
  Printer,
  MapPin,
  ShieldCheck,
  Link2,
  ExternalLink,
  Users,
  Clock,
  Hourglass,
  CheckCircle2,
  XCircle,
  CalendarDays,
  MapPinned,
  Info,
  FileText,
  ArrowLeft,
  CalendarCheck2,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface BadgeType {
  name: string
  count: number
  url: string
}

interface SessionCountRow {
  id: number
  dayNumber: number | null
  dayLabel: string | null
  time: string
  endTime: string
  description: string
  count: number
  listUrl: string
}

interface SessionCounts {
  enabled: boolean
  peopleWithSessions: number
  totalSelections: number
  sessions: SessionCountRow[]
}

interface DashboardProps {
  event: {
    id: number
    name: string
    eventDate: string | null
    eventTime: string | null
    about: string | null
    location: string | null
    spreadsheetKey: string | null
    active: boolean
  }
  counts: {
    all: number
    pending: number
    approved: number
    rejected: number
  }
  bundleCounts: {
    registrations: number
    ticketsIssued: number
    checkedInTickets: number
    approvedStudents: number
  }
  features: {
    emailWhitelist: boolean
    companionTickets: boolean
    bundleMetrics: boolean
    seatingMap: boolean
  }
  badgeTypes: BadgeType[]
  sessionCounts?: SessionCounts
  urls: {
    events: string
    accountSettings: string
    changePassword: string
    logout: string
    setup: string
    report: string
    badges: string
    zones: string
    reports: string
    approvedStudents: string
    registrations: string
    ticketsIssued: string
    checkedInTickets: string
    registrationLink: string
    listAll: string
    listPending: string
    listApproved: string
    listRejected: string
    whatsappTemplates?: string
    invitations?: string
    seatingMap: string
  }
  canEdit: boolean
  showBadges: boolean
  locale: string
  logoUrl: string | null
  accountName: string
  userEmail: string
  canEditAccount: boolean
  translations: {
    events: string
    accountSettings: string
    changePassword: string
    logout: string
    logoutConfirm: string
    logoutConfirmBody: string
    cancel: string
    whatsappTemplates?: string
    invitations?: string
    dashboard: string
    editEvent: string
    viewReport: string
    printBadges: string
    zones: string
    reports: string
    approvedStudents: string
    registrationLink: string
    registrations: string
    ticketsIssued: string
    checkedInTickets: string
    allRegistrations: string
    pendingRegistrations: string
    approvedRegistrations: string
    rejectedRegistrations: string
    withSessionSelection: string
    sessionSelections: string
    sessionSelectionsHint: string
    totalSelections: string
    selectionsLabel: string
    selectionLabel: string
    viewSessionList: string
    quickActions: string
    overview: string
    eventInfo: string
    eventDate: string
    eventTime: string
    location: string
    about: string
    backToEvents: string
    openSheet: string
    seatingMap: string
  }
}

/* ---- helpers ---- */

function formatDate(dateStr: string | null, locale: string) {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

function formatNumber(n: number) {
  return n.toLocaleString()
}

function formatSessionTime(time: string, endTime: string) {
  if (time && endTime) return `${time} – ${endTime}`
  return time || endTime || ''
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function EventDashboard({
  event,
  counts,
  bundleCounts,
  features,
  badgeTypes,
  sessionCounts,
  urls,
  canEdit,
  showBadges,
  locale,
  logoUrl,
  accountName,
  userEmail,
  canEditAccount,
  translations: t,
}: DashboardProps) {
  const showSessionCounts = Boolean(sessionCounts?.enabled)
  const kpiCards = [
    {
      label: t.allRegistrations,
      value: counts.all,
      icon: Users,
      color: '#2383E2',
      bgColor: '#E8F0FE',
      url: urls.listAll,
    },
    {
      label: t.pendingRegistrations,
      value: counts.pending,
      icon: Hourglass,
      color: '#91918E',
      bgColor: '#F1F1EF',
      url: urls.listPending,
    },
    {
      label: t.approvedRegistrations,
      value: counts.approved,
      icon: CheckCircle2,
      color: '#4B7A4B',
      bgColor: '#DBEDDB',
      url: urls.listApproved,
    },
    {
      label: t.rejectedRegistrations,
      value: counts.rejected,
      icon: XCircle,
      color: '#EB5757',
      bgColor: '#FBE4E4',
      url: urls.listRejected,
    },
    ...(showSessionCounts
      ? [
          {
            label: t.withSessionSelection,
            value: sessionCounts!.peopleWithSessions,
            icon: CalendarCheck2,
            color: '#8B5CF6',
            bgColor: '#F3E8FF',
            url: null as string | null,
          },
        ]
      : []),
  ]

  const sessionsByDay = showSessionCounts
    ? sessionCounts!.sessions.reduce<
        { key: string; dayLabel: string; total: number; sessions: SessionCountRow[] }[]
      >((groups, session) => {
        const key = String(session.dayNumber ?? session.dayLabel ?? 'day')
        const existing = groups.find((group) => group.key === key)
        if (existing) {
          existing.sessions.push(session)
          existing.total += session.count
          return groups
        }
        groups.push({
          key,
          dayLabel: session.dayLabel || key,
          total: session.count,
          sessions: [session],
        })
        return groups
      }, [])
    : []
  const maxSessionCount = showSessionCounts
    ? Math.max(1, ...sessionCounts!.sessions.map((session) => session.count))
    : 1

  const selectionWord = (count: number) =>
    count === 1 ? t.selectionLabel : t.selectionsLabel

  return (
    <>
      <Head title={`${event.name} — ${t.dashboard}`} />

      <AppShell
        locale={locale}
        logoUrl={logoUrl}
        accountName={accountName}
        userEmail={userEmail}
        canEditAccount={canEditAccount}
        urls={{
          events: urls.events,
          accountSettings: urls.accountSettings,
          changePassword: urls.changePassword,
          logout: urls.logout,
          whatsappTemplates: urls.whatsappTemplates,
          invitations: urls.invitations,
        }}
        translations={{
          events: t.events,
          accountSettings: t.accountSettings,
          changePassword: t.changePassword,
          logout: t.logout,
          logoutConfirm: t.logoutConfirm,
          logoutConfirmBody: t.logoutConfirmBody,
          cancel: t.cancel,
          whatsappTemplates: t.whatsappTemplates,
          invitations: t.invitations,
        }}
      >
        {/* ─── Back link ─── */}
        <div className="pt-4">
          <a
            href={urls.events}
            className="inline-flex items-center gap-1.5 text-sm text-[#91918E] hover:text-[#37352F] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t.backToEvents}
          </a>
        </div>

        {/* ─── Page header ─── */}
        <div className="pt-4 pb-1">
          <div className="flex items-center gap-2 mb-1">
            <div className="text-3xl">📊</div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-[#37352F] tracking-tight truncate">
                {event.name}
              </h1>
              <p className="text-sm text-[#91918E]">{t.dashboard}</p>
            </div>
            {event.active && (
              <span className="shrink-0 inline-flex items-center rounded-full bg-[#DBEDDB] px-2 py-0.5 text-xs font-medium text-[#4B7A4B]">
                Active
              </span>
            )}
          </div>
        </div>

        {/* ─── Quick actions ─── */}
        <div className="flex flex-wrap items-center gap-1.5 py-3 border-b border-[#E8E8E5]">
          {canEdit && (
            <a
              href={urls.setup}
              className="inline-flex items-center gap-1.5 rounded-[4px] px-2.5 py-1.5 text-xs font-medium text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
            >
              <Settings2 className="h-3.5 w-3.5 text-[#91918E]" />
              {t.editEvent}
            </a>
          )}
          <a
            href={urls.report}
            className="inline-flex items-center gap-1.5 rounded-[4px] px-2.5 py-1.5 text-xs font-medium text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
          >
            <BarChart3 className="h-3.5 w-3.5 text-[#91918E]" />
            {t.viewReport}
          </a>
          {showBadges && (
            <a
              href={urls.badges}
              className="inline-flex items-center gap-1.5 rounded-[4px] px-2.5 py-1.5 text-xs font-medium text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
            >
              <Printer className="h-3.5 w-3.5 text-[#91918E]" />
              {t.printBadges}
            </a>
          )}
          <a
            href={urls.zones}
            className="inline-flex items-center gap-1.5 rounded-[4px] px-2.5 py-1.5 text-xs font-medium text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
          >
            <MapPin className="h-3.5 w-3.5 text-[#91918E]" />
            {t.zones}
          </a>
          {features.seatingMap && (
            <a
              href={urls.seatingMap}
              className="inline-flex items-center gap-1.5 rounded-[4px] px-2.5 py-1.5 text-xs font-medium text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
            >
              <MapPinned className="h-3.5 w-3.5 text-[#91918E]" />
              {t.seatingMap}
            </a>
          )}
          {features.emailWhitelist && (
          <a
            href={urls.approvedStudents}
            className="inline-flex items-center gap-1.5 rounded-[4px] px-2.5 py-1.5 text-xs font-medium text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-[#91918E]" />
            {t.approvedStudents}
          </a>
          )}
          <a
            href={urls.registrationLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-[4px] px-2.5 py-1.5 text-xs font-medium text-[#2383E2] hover:bg-[#EBF5FF] transition-colors"
          >
            <Link2 className="h-3.5 w-3.5" />
            {t.registrationLink}
            <ExternalLink className="h-3 w-3 opacity-50" />
          </a>
        </div>

        {/* ─── KPI cards ─── */}
        <div className="py-5">
          <h2 className="text-xs font-medium text-[#91918E] uppercase tracking-wider mb-3">
            {t.overview}
          </h2>
          <div className={`grid gap-2 ${kpiCards.length > 4 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' : 'grid-cols-4'}`}>
            {kpiCards.map((card) => {
              const content = (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className="flex h-5 w-5 items-center justify-center rounded"
                      style={{ backgroundColor: card.bgColor }}
                    >
                      <card.icon className="h-3 w-3" style={{ color: card.color }} />
                    </div>
                    <span className="text-lg font-bold text-[#37352F] tabular-nums leading-none">
                      {formatNumber(card.value)}
                    </span>
                  </div>
                  <p className="text-[11px] text-[#91918E] group-hover:text-[#37352F] transition-colors truncate">
                    {card.label}
                  </p>
                </>
              )

              if (card.url) {
                return (
                  <a
                    key={card.label}
                    href={card.url}
                    className="group rounded-lg border border-[#E8E8E5] px-3 py-2.5 hover:shadow-sm hover:border-[#D3D3D0] transition-all"
                  >
                    {content}
                  </a>
                )
              }

              return (
                <div
                  key={card.label}
                  className="rounded-lg border border-[#E8E8E5] px-3 py-2.5"
                >
                  {content}
                </div>
              )
            })}
          </div>

          {/* Badge type cards */}
          {badgeTypes.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-2">
              {badgeTypes.map((badge) => (
                <a
                  key={badge.name}
                  href={badge.url}
                  className="group rounded-lg border border-[#E8E8E5] px-3 py-2.5 hover:shadow-sm hover:border-[#D3D3D0] transition-all"
                >
                  <p className="text-lg font-bold text-[#37352F] tabular-nums leading-none mb-1">
                    {formatNumber(badge.count)}
                  </p>
                  <p className="text-[11px] text-[#91918E] group-hover:text-[#37352F] transition-colors truncate">
                    {badge.name}
                  </p>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* ─── Session selections ─── */}
        {showSessionCounts && (
          <div className="pb-5">
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">
                  {t.sessionSelections}
                </h2>
                <p className="mt-1 text-sm text-[#91918E]">{t.sessionSelectionsHint}</p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-lg border border-[#E8E8E5] bg-white px-3 py-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#DBEDDB]">
                  <Users className="h-3.5 w-3.5 text-[#4B7A4B]" />
                </div>
                <div>
                  <p className="text-[11px] text-[#91918E]">{t.totalSelections}</p>
                  <p className="text-sm font-semibold tabular-nums text-[#37352F]">
                    {formatNumber(sessionCounts!.totalSelections)} {selectionWord(sessionCounts!.totalSelections)}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {sessionsByDay.map((group) => (
                <div key={group.key} className="rounded-lg border border-[#E8E8E5] overflow-hidden bg-white">
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#F7F7F5] border-b border-[#E8E8E5]">
                    <p className="text-xs font-medium text-[#37352F]">{group.dayLabel}</p>
                    <p className="text-xs tabular-nums text-[#91918E]">
                      {formatNumber(group.total)} {selectionWord(group.total)}
                    </p>
                  </div>
                  <div className="divide-y divide-[#F1F1EF]">
                    {group.sessions.map((session) => {
                      const timeLabel = formatSessionTime(session.time, session.endTime)
                      const fill = Math.round((session.count / maxSessionCount) * 100)
                      return (
                        <a
                          key={session.id}
                          href={session.listUrl}
                          title={t.viewSessionList}
                          className="flex items-center gap-4 px-4 py-3 hover:bg-[#F7F7F5] transition-colors"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-[#37352F] leading-snug">
                              {session.description || '—'}
                            </p>
                            {timeLabel && (
                              <p className="mt-0.5 text-xs tabular-nums text-[#91918E]">{timeLabel}</p>
                            )}
                          </div>
                          <div className="w-24 shrink-0 sm:w-36">
                            <div className="h-1.5 overflow-hidden rounded-full bg-[#E8E8E5]">
                              <div
                                className="h-full rounded-full bg-[#0b4f47]"
                                style={{ width: `${fill}%` }}
                              />
                            </div>
                          </div>
                          <div className="min-w-[4.5rem] shrink-0 text-end">
                            <p className="text-xl font-bold tabular-nums leading-none text-[#37352F]">
                              {formatNumber(session.count)}
                            </p>
                            <p className="mt-1 text-[11px] text-[#91918E]">{selectionWord(session.count)}</p>
                          </div>
                        </a>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Event information ─── */}
        <div className="pb-8">
          <h2 className="text-xs font-medium text-[#91918E] uppercase tracking-wider mb-3">
            {t.eventInfo}
          </h2>
          <div className="rounded-lg border border-[#E8E8E5] divide-y divide-[#F1F1EF]">
            {/* Date */}
            <div className="flex items-start gap-3 px-4 py-3">
              <CalendarDays className="h-4 w-4 text-[#91918E] mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-[#91918E]">{t.eventDate}</p>
                <p className="text-sm text-[#37352F]">{formatDate(event.eventDate, locale)}</p>
              </div>
            </div>

            {/* Time */}
            {event.eventTime && (
              <div className="flex items-start gap-3 px-4 py-3">
                <Clock className="h-4 w-4 text-[#91918E] mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-[#91918E]">{t.eventTime}</p>
                  <p className="text-sm text-[#37352F]">{event.eventTime}</p>
                </div>
              </div>
            )}

            {/* Location */}
            {event.location && (
              <div className="flex items-start gap-3 px-4 py-3">
                <MapPinned className="h-4 w-4 text-[#91918E] mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-[#91918E]">{t.location}</p>
                  <p className="text-sm text-[#37352F]">{event.location}</p>
                </div>
              </div>
            )}

            {/* About */}
            {event.about && (
              <div className="flex items-start gap-3 px-4 py-3">
                <Info className="h-4 w-4 text-[#91918E] mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-[#91918E]">{t.about}</p>
                  <p className="text-sm text-[#37352F] whitespace-pre-wrap">{event.about}</p>
                </div>
              </div>
            )}

            {/* Google Sheet */}
            {event.spreadsheetKey && (
              <div className="flex items-start gap-3 px-4 py-3">
                <FileText className="h-4 w-4 text-[#91918E] mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-[#91918E]">Google Sheet</p>
                  <a
                    href={`https://docs.google.com/spreadsheets/d/${event.spreadsheetKey}/edit`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[#2383E2] hover:underline mt-0.5"
                  >
                    {t.openSheet}
                    <ExternalLink className="h-3 w-3 opacity-50" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </AppShell>
    </>
  )
}
